<template>
  <master-layout :enable-scroll-event="true">
    <div class="flex flex-col w-screen h-[calc(100vh-69px)] bg-[#F6F5F1] font-[Inter] p-4">
      <h1 class="text-2xl font-bold mb-4">Homepage Debug</h1>

      <!-- Debug Information -->
      <div class="bg-white p-4 rounded-lg mb-4">
        <h2 class="text-lg font-semibold mb-2">Debug Information</h2>
        <div class="space-y-2">
          <div><strong>Authentication Status:</strong> {{ authStatus }}</div>
          <div><strong>Token Present:</strong> {{ hasToken ? 'Yes' : 'No' }}</div>
          <div><strong>Store State:</strong> {{ storeState }}</div>
          <div><strong>API URL:</strong> {{ apiUrl }}</div>
        </div>
      </div>

      <!-- Data Loading Status -->
      <div class="bg-white p-4 rounded-lg mb-4">
        <h2 class="text-lg font-semibold mb-2">Data Loading Status</h2>
        <div class="space-y-2">
          <div><strong>Middle Nav Icons:</strong> {{ middleNavIcons.length }} items</div>
          <div><strong>Featured Cards:</strong> {{ featuredCards.length }} items</div>
          <div><strong>Just For You:</strong> {{ justForYou.length }} items</div>
          <div><strong>All Animations:</strong> {{ allAnimations.length }} items</div>
          <div><strong>Church Updates:</strong> {{ featuredChurchUpdates.length }} items</div>
        </div>
      </div>

      <!-- Error Display -->
      <div v-if="errors.length > 0" class="bg-red-100 p-4 rounded-lg mb-4">
        <h2 class="text-lg font-semibold mb-2 text-red-800">Errors</h2>
        <div v-for="(error, index) in errors" :key="index" class="text-red-600">
          {{ error }}
        </div>
      </div>

      <!-- Simple Content -->
      <div class="bg-white p-4 rounded-lg">
        <h2 class="text-lg font-semibold mb-2">Simple Content Test</h2>
        <p>If you can see this, the basic component is working.</p>
        <button @click="testApiCall" class="bg-blue-500 text-white px-4 py-2 rounded mt-2">
          Test API Call
        </button>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'HomepageDebug',
  data() {
    return {
      errors: [],
      authStatus: 'Unknown',
      hasToken: false,
      storeState: 'Unknown',
      apiUrl: 'Unknown',
    };
  },
  computed: {
    ...mapState('homepage', ['middleNavIcons', 'featuredCards', 'justForYou', 'allAnimations']),
    ...mapState('churchUpdates', ['featuredChurchUpdates']),
  },
  async mounted() {
    console.log('HomepageDebug mounted');

    try {
      // Check authentication
      this.authStatus = this.$auth0?.isAuthenticated?.value ? 'Authenticated' : 'Not Authenticated';
      this.hasToken = !!localStorage.getItem('token');

      // Check store state
      this.storeState = this.$store ? 'Available' : 'Not Available';

      // Check API URL
      this.apiUrl = process.env.VUE_APP_API_URL || 'Not set';

      // Test data loading
      await this.loadData();
    } catch (error) {
      console.error('Error in HomepageDebug mounted:', error);
      this.errors.push(`Mounted error: ${error.message}`);
    }
  },
  methods: {
    async loadData() {
      try {
        console.log('Loading homepage data...');

        // Try to load homepage data
        await Promise.all([
          this.$store.dispatch('homepage/getMiddleNavIcons').catch(err => {
            this.errors.push(`Middle Nav Icons: ${err.message}`);
          }),
          this.$store.dispatch('homepage/getFeaturedCards').catch(err => {
            this.errors.push(`Featured Cards: ${err.message}`);
          }),
          this.$store.dispatch('homepage/getJustForYou').catch(err => {
            this.errors.push(`Just For You: ${err.message}`);
          }),
          this.$store.dispatch('homepage/getAllAnimations').catch(err => {
            this.errors.push(`All Animations: ${err.message}`);
          }),
          this.$store.dispatch('churchUpdates/getFeaturedChurchUpdates').catch(err => {
            this.errors.push(`Church Updates: ${err.message}`);
          }),
        ]);

        console.log('Data loading completed');
      } catch (error) {
        console.error('Error loading data:', error);
        this.errors.push(`Data loading error: ${error.message}`);
      }
    },

    async testApiCall() {
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/homepage/middle-nav-icons`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('API call successful:', data);
          alert('API call successful! Check console for details.');
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        console.error('API call failed:', error);
        alert(`API call failed: ${error.message}`);
      }
    },
  },
};
</script>
